export type PowerUpKind = "magnet" | "shield" | "nitro" | "x2";

export interface PowerUpState {
  magnetUntil: number;
  shield: boolean;
  nitroUntil: number;
  x2Until: number;
}

export const DURATIONS: Record<PowerUpKind, number> = {
  magnet: 8,
  nitro: 4,
  x2: 10,
  shield: 0, // toggled, no timer
};

/**
 * Time-based power-up state. `now` is a monotonic seconds clock owned by the
 * game scene (accumulated dt) — using performance.now would advance during
 * a pause, which we don't want.
 */
export class PowerUpSystem {
  private now = 0;
  private state: PowerUpState = {
    magnetUntil: -1,
    shield: false,
    nitroUntil: -1,
    x2Until: -1,
  };

  reset(): void {
    this.now = 0;
    this.state = {
      magnetUntil: -1,
      shield: false,
      nitroUntil: -1,
      x2Until: -1,
    };
  }

  tick(dt: number): void {
    this.now += dt;
  }

  apply(kind: PowerUpKind): void {
    if (kind === "shield") {
      this.state.shield = true;
      return;
    }
    const dur = DURATIONS[kind];
    const until = this.now + dur;
    if (kind === "magnet") this.state.magnetUntil = Math.max(this.state.magnetUntil, until);
    else if (kind === "nitro") this.state.nitroUntil = Math.max(this.state.nitroUntil, until);
    else if (kind === "x2") this.state.x2Until = Math.max(this.state.x2Until, until);
  }

  /** Returns true if shield was consumed; caller should ignore the hit. */
  consumeShield(): boolean {
    if (!this.state.shield) return false;
    this.state.shield = false;
    return true;
  }

  hasMagnet(): boolean {
    return this.state.magnetUntil > this.now;
  }
  hasShield(): boolean {
    return this.state.shield;
  }
  hasNitro(): boolean {
    return this.state.nitroUntil > this.now;
  }
  hasX2(): boolean {
    return this.state.x2Until > this.now;
  }

  /** 0..1 remaining for each timed power-up; 0 if expired. */
  remaining(kind: PowerUpKind): number {
    if (kind === "shield") return this.state.shield ? 1 : 0;
    const map = { magnet: this.state.magnetUntil, nitro: this.state.nitroUntil, x2: this.state.x2Until } as const;
    const until = map[kind];
    if (until <= this.now) return 0;
    return Math.min(1, (until - this.now) / DURATIONS[kind]);
  }
}
